import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type { SalesDoc, CreateSalesDocPayload, UpdateSalesDocPayload, Payment, CreatePaymentPayload } from '../types';

/** Canonical key — list page, detail drawer, and any future "Recent Docs" widget share it. */
export const SALES_DOCS_KEY = ['salesDocs', 'list'] as const;

export interface UseSalesDocsOptions {
  type?: string;
  folder?: string;
  relatedToType?: 'lead' | 'deal' | null;
  relatedToId?: string | null;
}

export function useSalesDocs(opts?: UseSalesDocsOptions) {
  return useQuery({
    queryKey: ['salesDocs', 'list', opts],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (opts?.type) params.append('type', opts.type);
      if (opts?.folder) params.append('folder', opts.folder);
      if (opts?.relatedToType) params.append('relatedToType', opts.relatedToType);
      if (opts?.relatedToId) params.append('relatedToId', opts.relatedToId);
      const q = params.toString() ? `?${params.toString()}` : '';
      const { data } = await api.get<{ docs: SalesDoc[] }>(`/api/sales-docs${q}`);
      return data.docs ?? [];
    },
    staleTime: 60_000,
  });
}

export function useFolders() {
  return useQuery({
    queryKey: ['salesDocs', 'folders'],
    queryFn: async () => {
      const { data } = await api.get<{ folders: string[] }>('/api/sales-docs/folders');
      return data.folders ?? [];
    },
    staleTime: 60_000,
  });
}

export function useSalesDoc(id?: string | null) {
  const { data: docs = [], isLoading, isError } = useSalesDocs();
  const doc = id ? docs.find((d) => d.id === id) ?? null : null;
  return { data: doc, isLoading, isError };
}

export function useSalesDocMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['salesDocs'] });

  const createDoc = useMutation({
    mutationFn: async (payload: CreateSalesDocPayload) => {
      const { data } = await api.post<{ ok: boolean; id: string }>('/api/sales-docs', payload, { skipErrorToast: true });
      return data;
    },
    onSuccess: invalidate,
  });

  const updateDoc = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateSalesDocPayload }) => {
      const { data } = await api.patch<{ ok: boolean }>(`/api/sales-docs/${id}`, payload, { skipErrorToast: true });
      return data;
    },
    onSuccess: invalidate,
  });

  const deleteDoc = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete<{ ok: boolean }>(`/api/sales-docs/${id}`, { skipErrorToast: true });
      return data;
    },
    onSuccess: invalidate,
  });

  return { createDoc, updateDoc, deleteDoc };
}

/** Optional file upload via the generic endpoint; returns the stored URL. */
export function useFileUpload() {
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post<{ url: string }>('/api/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        skipErrorToast: true,
      });
      return data.url;
    },
  });
}

export function usePayments(opts?: { invoiceId?: string }) {
  return useQuery({
    queryKey: ['payments', 'list', opts],
    queryFn: async () => {
      const q = opts?.invoiceId ? `?invoiceId=${opts.invoiceId}` : '';
      const { data } = await api.get<{ payments: Payment[] }>(`/api/payments${q}`);
      return data.payments ?? [];
    },
    staleTime: 30_000,
  });
}

export function usePaymentMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['payments'] });
    queryClient.invalidateQueries({ queryKey: ['salesDocs'] });
    queryClient.invalidateQueries({ queryKey: ['accountsData'] });
  };

  const logPayment = useMutation({
    mutationFn: async (payload: CreatePaymentPayload) => {
      const { data } = await api.post<{ ok: boolean; id: string }>('/api/payments', payload, { skipErrorToast: true });
      return data;
    },
    onSuccess: invalidate,
  });

  const deletePayment = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete<{ ok: boolean }>(`/api/payments/${id}`);
      return data;
    },
    onSuccess: invalidate,
  });

  return { logPayment, deletePayment };
}
